import { humanLabel, type JsonSchema } from "../records/records-api.js";

export type LogResource =
  | "assets"
  | "business_customers"
  | "cleaning"
  | "methods"
  | "staff"
  | "suppliers"
  | "water";
export type LogFieldKind =
  | "boolean"
  | "date"
  | "datetime"
  | "number"
  | "resource"
  | "select"
  | "text"
  | "textarea"
  | "time";

export interface LogField {
  defaultValue?: boolean | number | string;
  key: string;
  kind: LogFieldKind;
  label: string;
  max?: number;
  min?: number;
  // Sits behind "More details"; never required, still sent when filled.
  more?: boolean;
  options?: readonly { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  resource?: LogResource;
  step?: number | string;
}

export interface LogFormConfig {
  fields: readonly LogField[];
  repeatable?: {
    fields: readonly LogField[];
    key: string;
    label: string;
    minimum: number;
  };
}

const option = (value: string, label = humanLabel(value)) => ({ label, value });
const text = (
  key: string,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "text", label, required });
const area = (
  key: string,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "textarea", label, required });
const number = (
  key: string,
  label = humanLabel(key),
  required = false,
  min?: number,
  max?: number,
): LogField => ({
  key,
  kind: "number",
  label,
  required,
  ...(min === undefined ? {} : { min }),
  ...(max === undefined ? {} : { max }),
  step: "any",
});
const date = (
  key: string,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "date", label, required });
const datetime = (
  key: string,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "datetime", label, required });
const time = (
  key: string,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "time", label, required });
const bool = (
  key: string,
  label = humanLabel(key),
  defaultValue = false,
): LogField => ({ defaultValue, key, kind: "boolean", label });
const select = (
  key: string,
  values: readonly string[],
  label = humanLabel(key),
  required = true,
  labels: Readonly<Record<string, string>> = {},
  placeholder?: string,
  defaultValue?: string,
): LogField => ({
  key,
  kind: "select",
  label,
  options: values.map((value) => option(value, labels[value])),
  required,
  ...(placeholder === undefined ? {} : { placeholder }),
  ...(defaultValue === undefined ? {} : { defaultValue }),
});
const resource = (
  key: string,
  source: LogResource,
  label = humanLabel(key),
  required = false,
): LogField => ({ key, kind: "resource", label, required, resource: source });

const proving: readonly LogField[] = [
  area("food_description", "Food or product", true),
  resource("process_method_id", "methods", "Process method"),
  text("batch_no", "Batch number"),
  bool("is_batch_check", "This is a batch check", true),
  bool("is_proving", "This is a proving run"),
].map((field) =>
  field.required || field.key === "food_description"
    ? field
    : { ...field, more: true },
);

export const incidentCategoryLabels: Readonly<Record<string, string>> = {
  contamination: "Contamination",
  equipment: "Equipment",
  other: "Other",
  pest: "Pest",
  power_outage: "Power outage",
  supplier: "Supplier",
  temperature: "Temperature",
};

export const temperatureKindLabels: Readonly<Record<string, string>> = {
  chill_air: "Fridge air",
  chill_food: "Chilled food",
  display: "Display",
  freezer: "Freezer",
  hot_hold: "Hot hold",
};

const reportabilityLabels: Readonly<Record<string, string>> = {
  not_assessed: "Not assessed",
  not_reportable: "Not reportable",
  reportable: "Reportable",
  reported: "Reported",
};

// Fields that sit behind "More details", by record type.
const moreKeys: Readonly<Record<string, readonly string[]>> = {
  audit: ["period_start", "period_end", "summary"],
  b2b_supply: ["quantity", "batch_lot"],
  complaint: [
    "customer_name",
    "customer_contact",
    "purchased_at_text",
    "purchased_on",
    "batch_lot",
    "suspected_cause",
    "immediate_action",
    "prevention",
    "resolved_by_staff_profile_id",
  ],
  cook: ["held_minutes"],
  incident: [
    "fix_action",
    "prevention",
    "food_kept_safe",
    "staff_involved",
    "responsible_staff_profile_id",
    "reportability",
    "reported_at",
  ],
  maintenance: ["provider", "next_due_on"],
  sickness: ["returned_on", "action_taken", "clearance_note"],
  temperature: ["device_asset_id"],
  traceability: [
    "manufacturer_details",
    "brand",
    "best_before_text",
    "arrival_temp_c",
    "batch_lot",
    "other_information",
  ],
  transport: ["minutes_out_of_control", "vehicle_asset_id", "destination"],
};

// Which equipment type suggests which temperature check.
export function temperatureKindForAsset(
  assetType: string | undefined,
): string | undefined {
  switch (assetType) {
    case "fridge":
    case "chiller":
      return "chill_air";
    case "freezer":
      return "freezer";
    case "hot_hold":
      return "hot_hold";
    default:
      return undefined;
  }
}

const baseLogForms: Readonly<Record<string, LogFormConfig>> = {
  temperature: {
    fields: [
      resource("asset_id", "assets", "Equipment", true),
      select(
        "kind",
        ["chill_air", "chill_food", "freezer", "hot_hold", "display"],
        "Check type",
        true,
        temperatureKindLabels,
      ),
      number("value_c", "Temperature °C", true),
      resource("device_asset_id", "assets", "Probe or device"),
    ],
  },
  delivery: {
    fields: [resource("supplier_id", "suppliers", "Supplier", true)],
    repeatable: {
      key: "items",
      label: "Delivery item",
      minimum: 1,
      fields: [
        text("food_description", "Food", true),
        number("arrival_temp_c", "Arrival temperature °C"),
        bool("accepted", "Accepted", true),
        area("rejection_reason", "Rejection reason"),
        { ...text("quantity"), more: true },
        { ...text("batch_lot", "Batch or lot"), more: true },
      ],
    },
  },
  cook: {
    fields: [
      select(
        "cook_kind",
        ["poultry_minced_liver", "doner", "other"],
        "Cooking method",
        true,
        { poultry_minced_liver: "Poultry, mince or liver" },
      ),
      ...proving,
      number("internal_temp_c", "Internal temperature °C", true),
      number("held_minutes", "Time held at temperature (minutes)", false, 0),
    ],
  },
  reheat: {
    fields: [
      ...proving,
      number("internal_temp_c", "Internal temperature °C", true),
    ],
  },
  cooling: {
    fields: [
      select("variant", ["general", "sushi_rice"], "Cooling method"),
      ...proving,
      datetime("start_at", "Cooling started", true),
      number("start_temp_c", "Starting temperature °C", true),
      datetime("second_at", "Second check time"),
      number("second_temp_c", "Second temperature °C"),
      datetime("third_at", "Third check time"),
      number("third_temp_c", "Third temperature °C"),
    ],
  },
  water_activity: {
    fields: [
      ...proving,
      date("started_on", "Started on", true),
      date("finished_on", "Finished on"),
      number("weight_before_g", "Weight before (g)", false, 0),
      number("weight_after_g", "Weight after (g)", false, 0),
      number("meter_reading_aw", "Meter reading aw", false, 0, 1),
      number("lab_result_aw", "Lab result aw", false, 0, 1),
    ],
  },
  acidification: {
    fields: [
      select("acid_kind", ["pickling", "sushi_rice"], "Acid control"),
      ...proving,
      date("started_on", "Started on", true),
      date("finished_on", "Finished on"),
      number("ph_start", "Starting pH", false, 0, 14),
      number("ph_finish", "Final pH", true, 0, 14),
    ],
  },
  hot_smoke: {
    fields: [
      select("smoke_kind", ["cook", "flavour"], "Smoking purpose"),
      ...proving,
      number("smokehouse_temp_c", "Smokehouse temperature °C", true),
      time("started_at_local", "Started at"),
      time("finished_at_local", "Finished at"),
      number("duration_minutes", "Duration (minutes)", false, 0),
      number("core_temp_end_c", "Final core temperature °C"),
      bool("more_time_needed", "More time was needed"),
      area("action_taken"),
    ],
  },
  sous_vide: {
    fields: [
      ...proving,
      number("bath_temp_before_c", "Bath temperature before °C", true),
      number(
        "minutes_to_internal",
        "Minutes to internal temperature",
        false,
        0,
      ),
      number("internal_temp_start_c", "Starting internal temperature °C"),
      number("holding_minutes", "Holding time (minutes)", false, 0),
      number("internal_temp_end_c", "Final internal temperature °C"),
      number("cooling_minutes", "Cooling time (minutes)", false, 0),
      bool("served_immediately", "Served immediately"),
      area("chilled_storage", "Chilled storage details"),
    ],
  },
  duck_drying: {
    fields: [
      resource("process_method_id", "methods", "Process method"),
      text("batch_no", "Batch number"),
      time("started_at_local", "Started at", true),
      number("temp_start_c", "Starting temperature °C", true),
      number("temp_halfway_c", "Halfway temperature °C"),
      time("finished_at_local", "Finished at"),
      area("action_if_over_limit", "Action if over limit"),
      bool("is_batch_check", "This is a batch check", true),
      bool("is_proving", "This is a proving run"),
    ],
  },
  transport: {
    fields: [
      area("food_description", "Food description", true),
      number("temp_c", "Temperature °C", true),
      number(
        "minutes_out_of_control",
        "Minutes out of temperature control",
        false,
        0,
      ),
      resource("vehicle_asset_id", "assets", "Vehicle"),
      text("destination"),
    ],
  },
  calibration: {
    fields: [
      resource("thermometer_asset_id", "assets", "Thermometer", true),
      number("ice_reading_c", "Ice-point reading °C"),
      number("boiling_reading_c", "Boiling-point reading °C"),
      area("action_taken"),
    ],
  },
  maintenance: {
    fields: [
      resource("asset_id", "assets", "Equipment"),
      text("target_description", "Other item or facility"),
      select(
        "work_kind",
        [
          "check",
          "repair",
          "service",
          "pest_service",
          "water_system",
          "calibration_done",
        ],
        "Work type",
      ),
      area("what_was_done", "What was done", true),
      text("provider"),
      date("next_due_on", "Next due on"),
    ],
  },
  cleaning: {
    fields: [
      resource("cleaning_plan_item_id", "cleaning", "Cleaning plan item"),
      area("task_description", "What was cleaned", true),
      select(
        "result",
        ["done", "exception"],
        "Result",
        true,
        {},
        undefined,
        "done",
      ),
      area("exception_note", "Exception note"),
    ],
  },
  sickness: {
    fields: [
      resource("staff_profile_id", "staff", "Staff member", true),
      date("became_sick_on", "Became sick on", true),
      date("returned_on", "Returned on"),
      bool("excluded_from_food_handling", "Excluded from food handling", true),
      area("action_taken"),
      area("clearance_note"),
    ],
  },
  incident: {
    fields: [
      select(
        "category",
        [
          "equipment",
          "temperature",
          "contamination",
          "pest",
          "supplier",
          "power_outage",
          "other",
        ],
        "Category",
        true,
        incidentCategoryLabels,
        "Choose a category",
      ),
      area("what_went_wrong", "What went wrong", true),
      area("fix_action", "Immediate fix"),
      area("prevention"),
      area("food_kept_safe", "How food was kept safe"),
      area("staff_involved"),
      resource("responsible_staff_profile_id", "staff", "Responsible person"),
      select(
        "reportability",
        ["not_assessed", "not_reportable", "reportable", "reported"],
        "Reportability",
        false,
        reportabilityLabels,
      ),
      datetime("reported_at", "Reported at"),
    ],
  },
  complaint: {
    fields: [
      text("customer_name"),
      text("customer_contact"),
      text("purchased_at_text", "Purchase details"),
      date("purchased_on", "Purchased on"),
      area("affected_food", "Affected food", true),
      text("batch_lot", "Batch or lot"),
      area("complaint", "Complaint details", true),
      area("suspected_cause"),
      area("immediate_action"),
      area("prevention"),
      bool("illness_claimed", "Illness claimed"),
      resource("resolved_by_staff_profile_id", "staff", "Resolved by"),
    ],
  },
  b2b_supply: {
    fields: [
      resource(
        "business_customer_id",
        "business_customers",
        "Business customer",
        true,
      ),
      area("food_supplied", "Food supplied", true),
      text("quantity"),
      date("supplied_on", "Supplied on", true),
      text("batch_lot", "Batch or lot"),
    ],
  },
  audit: {
    fields: [
      area("scope", "Audit scope", true),
      date("period_start", "Period start"),
      date("period_end", "Period end"),
      area("summary"),
    ],
    repeatable: {
      key: "items",
      label: "Audit item",
      minimum: 1,
      fields: [
        area("check_item", "Check item", true),
        select("result", ["ok", "issue", "na"], "Result", true, {
          ok: "OK",
          na: "Not applicable",
        }),
        area("finding"),
        area("action_required"),
        select("severity", ["minor", "major", "critical"], "Severity", false),
      ],
    },
  },
  water_test: {
    fields: [
      resource("water_source_id", "water", "Water source", true),
      datetime("sampled_at", "Sampled at", true),
      text("sample_location"),
      text("laboratory"),
      text("ecoli_result", "E. coli result"),
      number("turbidity_ntu", "Turbidity NTU", false, 0),
      number("free_chlorine_mg_l", "Free chlorine mg/L", false, 0),
      number("ph", "pH", false, 0, 14),
      bool("passed", "Test passed"),
      date("retest_due_on", "Retest due on"),
    ],
  },
  traceability: {
    fields: [
      resource("supplier_id", "suppliers", "Supplier"),
      area("supplier_details", "Other supplier details"),
      area("manufacturer_details"),
      area("product_type", "Product type", true),
      text("brand"),
      text("best_before_text", "Best-before details"),
      number("arrival_temp_c", "Arrival temperature °C"),
      text("batch_lot", "Batch or lot"),
      area("other_information"),
    ],
  },
  recall: {
    fields: [
      select("recall_kind", ["supplier_recall", "own_recall"], "Recall type"),
      text("trigger_source"),
      area("affected_products", "Affected products", true),
      area("affected_batches"),
      area("actions_taken"),
      text("quantities"),
      area("risk_assessment"),
      text("notice_reference"),
      datetime("mpi_notified_at", "MPI notified at"),
      area("effectiveness_note"),
    ],
  },
};

export const typedLogForms: Readonly<Record<string, LogFormConfig>> =
  Object.fromEntries(
    Object.entries(baseLogForms).map(([code, form]) => {
      const keys = moreKeys[code] ?? [];
      return [
        code,
        {
          ...form,
          fields: form.fields.map((field) =>
            keys.includes(field.key) && !field.required
              ? { ...field, more: true }
              : field,
          ),
        },
      ];
    }),
  );

function fieldFromSchema(
  key: string,
  schema: JsonSchema,
  required: boolean,
): LogField {
  if (schema.enum)
    return {
      key,
      kind: "select",
      label: humanLabel(key),
      options: schema.enum.map((value) => option(value)),
      required,
    };
  if (schema.type === "boolean") return bool(key);
  if (schema.type === "number" || schema.type === "integer")
    return {
      ...number(key, humanLabel(key), required, schema.minimum, schema.maximum),
      step: schema.type === "integer" ? 1 : "any",
    };
  if (schema.format === "date") return date(key, humanLabel(key), required);
  if (schema.format === "date-time")
    return datetime(key, humanLabel(key), required);
  return schema.maxLength !== undefined && schema.maxLength > 500
    ? area(key, humanLabel(key), required)
    : text(key, humanLabel(key), required);
}

export function formFromJsonSchema(schema: JsonSchema): LogFormConfig {
  const required = new Set(schema.required ?? []);
  const fields: LogField[] = [];
  // Conditionally required fields cannot be told apart, so none are tucked away.
  const keepOptional =
    required.size === 0 ||
    Boolean(schema.if || schema.then || "allOf" in schema || "oneOf" in schema);
  let repeatable: LogFormConfig["repeatable"];
  for (const [key, property] of Object.entries(schema.properties ?? {})) {
    if (property.type === "array" && property.items?.type === "object") {
      repeatable = {
        fields: Object.entries(property.items.properties ?? {}).map(
          ([itemKey, itemSchema]) =>
            fieldFromSchema(
              itemKey,
              itemSchema,
              new Set(property.items?.required ?? []).has(itemKey),
            ),
        ),
        key,
        label: humanLabel(key).replace(/s$/, ""),
        minimum: property.minItems ?? 0,
      };
    } else if (property.type === "array") {
      fields.push({
        ...area(key, `${humanLabel(key)} (one per line)`),
        ...(keepOptional ? {} : { more: true }),
      });
    } else {
      const field = fieldFromSchema(key, property, required.has(key));
      fields.push(
        field.required || keepOptional ? field : { ...field, more: true },
      );
    }
  }
  return { fields, ...(repeatable ? { repeatable } : {}) };
}

export function categoryLabel(category: string): string {
  const labels: Record<string, string> = {
    cleaning_closing: "Cleaning & closing",
    making_cooking: "Making & cooking",
    preparation: "Preparation",
    serving_selling: "Serving & selling",
    setup: "People & review",
    specialist: "Specialist processes",
    starting: "Starting the day",
    troubleshooting: "Problems & traceability",
  };
  return labels[category] ?? humanLabel(category);
}

export function recordTypeTitle(type: { code: string; title: string }): string {
  if (type.code === "incident") return "Report a problem";
  const short = type.title
    .replace(/\s*\(.*$/, "")
    .split(" / ")[0]!
    .trim();
  return short || type.title;
}
